import {createSelector} from '@reduxjs/toolkit';
import {FOLDERS_TILES_URL, ITEMS_TILES_URL, TILES_HOSTNAME} from '../config.js';
import {getFullCompareSubjects} from './compare.js';
import {getSelectedItems, getSelectedResults} from './folders.js';
import {getRulesForMosaic, getSelectedMosaic} from './mosaics.js';
import {getSelectedViewItems, getSelectedViewResults} from './results.js';
import {getSearchGeometryGeoJson} from './search.js';
import {
  COMPARE_TYPES,
  DOWNSAMPLE_ZOOM,
  LABS_MAP,
  MAX_FOLDERS_ZOOM,
  MAX_FULL_ACCESS_ZOOM,
  MAX_TILESET_ITEMS,
  MAX_VISIBLE_RESULTS,
  TILE_MODE,
} from '@explorer/constants';
import {getExtent} from '@explorer/util/geom';
import {
  getUniqueId,
  getUniqueIdItems,
  itemTypeSupported,
} from '@explorer/util/items';
import {getLimitedZoomForItem, getMaxZoomForItem} from '@explorer/util/map';

const getTileMode = state => state.tiles.mode;
const getModeSettings = state => state.tiles.modeSettings;
export const getProcess = state =>
  state.tiles.modeSettings[state.tiles.mode].process;
export const getProcessColor = state =>
  state.tiles.modeSettings[state.tiles.mode].processColor;

const getWebpEnabled = state => !!state.labs[LABS_MAP.WEBP_MOSAIC_TILES];

const getSelectedFolderId = state => state.folders.selectedFolderId;

const TILE_ENCODINGS = {
  JPEG: '.jpeg',
  PNG: '.png',
  WEBP: '.webp',
  NPY: '.npy',
};

const removeQueryParam = (url, paramName) => {
  try {
    const newURL = new URL(url);
    // remove API Key to guarantee injecting a fresh one
    newURL.searchParams.delete(paramName);

    return decodeURIComponent(newURL.toString());
  } catch {
    return '';
  }
};

const addQueryParams = (url, params) => {
  try {
    const newURL = new URL(url);
    const searchParams = newURL.searchParams;

    Object.keys(params).forEach(param => {
      if (params[param]) {
        searchParams.set(param, params[param]);
      }
    });

    return decodeURIComponent(newURL.toString());
  } catch {
    return '';
  }
};

const getUrlTemplate = (url, format) => {
  // remove API Key to guarantee injecting a fresh one
  const newURL = removeQueryParam(url, 'api_key');

  let template = newURL.replace(
    /http(.?):\/\/tiles.planet.com/,
    TILES_HOSTNAME
  );

  if (format) {
    template = template.replace(TILE_ENCODINGS.PNG, format);
  }

  return template;
};

const getSearchGeoExtent = createSelector(
  getSearchGeometryGeoJson,
  searchGeometry => {
    if (searchGeometry) {
      const extent = getExtent(searchGeometry);
      return [extent.left, extent.bottom, extent.right, extent.top];
    }
    return [];
  }
);

const getMosaicTileConfigs = (
  mosaic,
  mosaicRules,
  useWebp,
  process,
  processColor,
  token
) => {
  if (!mosaic) {
    return;
  }
  const mosaicAOIs = mosaicRules.some(rule => rule.worldIntersect > 0.75)
    ? []
    : mosaicRules.map(rule => rule.aoiGeo);
  const url = getUrlTemplate(
    mosaic._links?.tiles,
    useWebp ? TILE_ENCODINGS.WEBP : ''
  );

  const baseOptions = {
    bands: mosaic.bands,
    datatype: mosaic.datatype,
    pixelDepth: mosaic.pixelDepth,
    numpyUrl: addQueryParams(
      getUrlTemplate(mosaic._links?.tiles, TILE_ENCODINGS.NPY),
      {proc: 'off', token}
    ),
  };

  const sourceConfig = {
    id: mosaic.seriesId, //makes for a smoother transition in the same series
    mosaicId: mosaic.id,
    url: addQueryParams(url, {token}),
    maxZoom: mosaic.level || MAX_FULL_ACCESS_ZOOM,
    maxLimitedZoom: DOWNSAMPLE_ZOOM,
  };

  if (process) {
    sourceConfig.url = addQueryParams(sourceConfig.url, {
      proc: process,
      color: processColor,
    });
  }

  const aoiToConfig = (mosaicAOI, idxAOI) =>
    mosaicAOI.coordinates.map((coord, idx) => {
      const extent = getExtent({
        type: mosaicAOI.type,
        coordinates: [coord],
      });

      return {
        ...sourceConfig,
        id: `${mosaic.seriesName}-${idxAOI}-${idx}`,
        extent: [extent.left, extent.bottom, extent.right, extent.top],
      };
    });

  const flatten = (acc = [], cur = []) => cur.concat(acc);

  const mosaicSources =
    mosaicAOIs.length > 0
      ? mosaicAOIs.map(aoiToConfig).reduce(flatten)
      : [sourceConfig];

  return {
    ...baseOptions,
    rasterConfigs: mosaicSources,
  };
};

const getFolderResultConfig = ({
  result,
  limit,
  useWebp,
  selectedItems,
  token,
  folderId,
  process,
  processColor,
}) => {
  const configs = [];

  let allItems = result.properties.items;

  if (selectedItems) {
    allItems = allItems.filter(item => {
      return selectedItems.includes(item.id);
    });
  }

  const maxZoom = Math.min(...allItems.map(getMaxZoomForItem));
  const itemType = allItems[0].properties.item_type;
  const queryParams = {};

  if (itemTypeSupported(itemType)) {
    queryParams.proc = process || 'rgb';
  }
  if (processColor) {
    queryParams.color = processColor;
  }

  for (let idx = 0; idx < allItems.length; idx += limit) {
    const items = allItems.slice(idx, idx + limit);

    const itemIds = items
      .reverse()
      .map(item => item.id)
      .join();

    const url = `${FOLDERS_TILES_URL}/folder-${folderId}/${itemIds}/{z}/{x}/{y}${
      useWebp ? TILE_ENCODINGS.WEBP : TILE_ENCODINGS.PNG
    }`;

    const config = {
      id: items.map(item => item.id).join(),
      resultId: result.id,
      url: addQueryParams(url, {token, ...queryParams}),
      maxZoom,
      maxLimitedZoom: MAX_FOLDERS_ZOOM,
    };

    configs.push(config);
  }
  return configs;
};

const getFolderTileConfigs = (
  folderId,
  selectedResults,
  selectedItems,
  useWebp,
  process,
  processColor,
  token
) => {
  let baseOptions = {};
  const sourceConfigs = [];

  if (!selectedResults.length || !folderId) {
    return;
  }

  const itemTypes = Array.from(
    new Set(selectedResults.map(result => result.properties.item_type))
  );

  // numpy is not supported for multiple item types
  if (itemTypes.length === 1) {
    const allItems = selectedResults
      .map(result => result.properties.items)
      .flat()
      .filter(item => selectedItems.includes(item.id));
    const {bands, datatype, pixelDepth} = allItems[0].properties;

    const allIds = allItems
      .map(item => item.id)
      .reverse()
      .join(',');
    const numpyUrl = `${FOLDERS_TILES_URL}/folder-${folderId}/${allIds}/{z}/{x}/{y}${TILE_ENCODINGS.NPY}?token=${token}&proc=off`;

    baseOptions = {
      ...baseOptions,
      bands,
      datatype,
      pixelDepth,
      numpyUrl,
    };
  }

  selectedResults.forEach(result => {
    const configs = getFolderResultConfig({
      result,
      limit: 10,
      useWebp,
      selectedItems,
      token,
      folderId,
      process,
      processColor,
    });
    configs.forEach(config => sourceConfigs.unshift(config));
  });

  return {
    ...baseOptions,
    rasterConfigs: sourceConfigs,
  };
};
const getResultConfig = ({
  result,
  limit,
  useWebp,
  selectedItems,
  token,
  extent,
}) => {
  const configs = [];

  let allItems = result.properties.items;

  if (selectedItems) {
    allItems = allItems.filter(item => {
      const uniqueId = getUniqueId(item);
      return selectedItems.includes(uniqueId);
    });
  }

  // Note: we use the most restricted zoom level for any of the items
  // in a result.
  const maxZoom = Math.min(...allItems.map(item => getMaxZoomForItem(item)));

  const maxLimitedZoom = Math.min(
    ...allItems.map(item => getLimitedZoomForItem(item))
  );

  const itemType = result.properties.item_type;

  for (let idx = 0; idx < allItems.length; idx += limit) {
    const items = allItems.slice(idx, idx + limit);

    const itemIds = items
      .reverse()
      .map(item => item.id)
      .join();

    const url = `${ITEMS_TILES_URL}/${itemType}/${itemIds}/{z}/{x}/{y}${
      useWebp ? TILE_ENCODINGS.WEBP : TILE_ENCODINGS.PNG
    }`;

    const config = {
      id: getUniqueIdItems(items),
      resultId: result.id,
      url: addQueryParams(url, {token}),
      maxZoom,
      maxLimitedZoom,
      downsample: items.some(
        item => !(item.properties.canDownload || item.properties.canStream) // OR due to transition as not all plans include the streaming perms explicitly
      ),
      extent,
    };

    configs.push(config);
  }
  return configs;
};

const getResultsTileConfigs = (
  selectedResults,
  selectedItems,
  useWebp,
  extent,
  token
) => {
  if (!selectedResults?.length) {
    return;
  }
  const sourceConfigs = [];
  const filteredResults = selectedResults.slice(0, MAX_VISIBLE_RESULTS);

  filteredResults.forEach(result => {
    const configs = getResultConfig({
      result,
      limit: MAX_TILESET_ITEMS,
      useWebp,
      selectedItems,
      token,
      extent,
    });

    configs.forEach(config => sourceConfigs.unshift(config));
  });

  return {
    rasterConfigs: sourceConfigs,
  };
};

export const getTileConfigs = createSelector(
  getSelectedViewResults,
  getSelectedViewItems,
  getWebpEnabled,
  getSearchGeoExtent,
  getSelectedMosaic,
  getRulesForMosaic,
  getProcess,
  getProcessColor,
  getSelectedFolderId,
  getSelectedResults,
  getSelectedItems,
  (_, auth) => auth,

  (
    selectedResults,
    selectedItems,
    useWebp,
    extent,
    selectedMosaic,
    mosaicRules,
    process,
    processColor,
    selectedFolderId,
    selectedFolderResults,
    selectedFolderItems,
    auth
  ) => {
    const token = auth?.user.accessToken;

    return {
      [TILE_MODE.FOLDERS]: getFolderTileConfigs(
        selectedFolderId,
        selectedFolderResults,
        selectedFolderItems,
        useWebp,
        process,
        processColor,
        token
      ),
      [TILE_MODE.BASEMAPS]: getMosaicTileConfigs(
        selectedMosaic,
        mosaicRules,
        useWebp,
        process,
        processColor,
        token
      ),
      [TILE_MODE.RESULTS]: getResultsTileConfigs(
        selectedResults,
        selectedItems,
        useWebp,
        extent,
        token
      ),
    };
  }
);

export const getCompareSubjectsConfigs = createSelector(
  getFullCompareSubjects,
  getWebpEnabled,
  getProcess,
  getProcessColor,
  getSearchGeoExtent,
  (_, auth) => auth,
  (compareSubjects, useWebp, process, processColor, extent, auth) => {
    const token = auth?.user.accessToken;

    const configs = [];
    Object.entries(compareSubjects).forEach(([index, subject]) => {
      if (subject) {
        if (
          subject.type === COMPARE_TYPES.RESULT ||
          subject.type === COMPARE_TYPES.SCENE
        ) {
          const resultConfig = getResultConfig({
            result: subject.item,
            limit: MAX_TILESET_ITEMS,
            useWebp,
            token,
            extent,
          });
          configs.splice(index, 1, {
            rasterConfigs: resultConfig,
          });
        } else if (subject.type === COMPARE_TYPES.MOSAIC) {
          const mosaicConfig = getMosaicTileConfigs(
            subject.item,
            [],
            useWebp,
            process,
            processColor,
            token
          );
          configs.splice(index, 1, mosaicConfig);
        }
      }
    });
    return configs;
  }
);

export const getEnhanceFilters = createSelector(
  [getTileMode, getModeSettings],
  (tileMode, modeSettings) => modeSettings[tileMode].enhanceFilters
);

export const getEnhanceMode = createSelector(
  [getTileMode, getModeSettings],
  (tileMode, modeSettings) => modeSettings[tileMode].enhanceMode
);
